import { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import NumberFormat from "react-number-format";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import ReadMore from "components/UpStyled/ReadMore";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import SuiTagInput from "components/SuiTagInput";

import { Upload, DatePicker } from "antd";
import { getBase64, dataURLtoFile, handleUploadFiles, beforeUpload } from "utils/imageHelper";

import { isInPortfolio, checkPermission } from "utils/helper";
import DropZone from "components/UpStyled/DropZone";

import moment from "moment";

import toast from "react-hot-toast";
import api from "services/api";

import CategoryContext from "context/category-context";
import SharedContentContext from "context/shared-content";
import { AccountContext } from "context/account";

import { seasonOptions } from "utils/data";
import CreateCollectionModal from "pages/de/collection/components/CreateCollectionModal";
import FilePreviewCard from "components/UpStyled/FilePreviewCard";
import UTreeSelect from "components/UpStyled/UTreeSelect";
import FullCreateStyleModal from "pages/de/project/create-project/components/FullCreateStyleModal";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Comments from "pages/comment";
import Prompt from "components/UpStyled/Prompt";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import CreateColorModal from "pages/de/palette/components/CreateColorModal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

var deepEqual = require("deep-equal");

const STYLE_DESC_MAX_LENGTH = 2000;
const MAX_STYLE_NUMBER_COUNT = 12;
const MAX_TAG_LENGTH = 40;

const DesignStage = forwardRef(
  (
    {
      detail,
      editable = false,
      handleRefresh,
      accountType,
      sharedView = false,
      handleNext = () => {},
    },
    ref
  ) => {
    const [imageFiles, setImageFiles] = useState([]);
    const [collectionOptions, setCollectionOptions] = useState([]);
    const [styleDesc, setStyleDesc] = useState("");
    const [uploading, setUploading] = useState(false);
    const [collectionsInfo, setCollectionsInfo] = useState([]);
    const [createCollectionVisible, setCreateCollectionVisible] = useState(false);
    const [duplicateStyleVisible, setDuplicateStyleVisible] = useState(false);
    const [detailImage, setDetailImage] = useState(null);
    const [colorOptions, setColorOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [colorDetailVisible, setColorDetailVisible] = useState(false);
    const [selectedColor, setSelectedColor] = useState({});
    const [selectedGender, setSelectedGender] = useState(undefined);
    const [categoryOptions, setCategoryOptions] = useState([]);

    const [imgsViewerVisible, setImgsViewerVisible] = useState(false);
    const [imgsViewerCurrent, setImgsViewerCurrent] = useState(0);

    const { permissionMap, retrieveContentPermission } = useContext(SharedContentContext);

    const urlParams = new URLSearchParams(window.location.search);
    let styleId = urlParams.get("id");

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    useImperativeHandle(ref, () => ({
      getValues: getValues,
      handleUpdateStyle: handleUpdateStyle,
    }));

    const {
      tree,
      actions: { refreshCategoryTree, findParents, findNodeByID },
    } = useContext(CategoryContext);
    const account = useContext(AccountContext);

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        if (name == "sizeChart" && type == "change") {
          if (value.sizeChart?.meta?.sizes) {
            setValue("sizeRange", value.sizeChart?.meta?.sizes);
          }
        }
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
      retrieveCollections();
      retrievePalette();
      retrieveSizeCharts();
    }, [detail.id]);

    useEffect(() => {
      if (tree && detail.id) {
        handleReset();
      }
    }, [detail.id, tree]);

    useEffect(() => {
      let oldImageFiles = detail?.style?.imageFiles || [];
      const isEqual = deepEqual(oldImageFiles, imageFiles);
      if (!isEqual && imageFiles.length > 0) {
        console.log("trigger:", oldImageFiles, imageFiles, isEqual);
        if (imageFiles.length > 0) {
          setDetailImage(imageFiles[imageFiles.length - 1]);
        } else {
          setDetailImage(null);
        }

        updateImageFiles();
      }
    }, [imageFiles]);

    const retrieveCollections = async () => {
      try {
        const { data } = await api.collections.list({ order: "-updated_at" });

        const collections = data["collections"] ? data["collections"] : [];
        const options = collections.map((x) => ({ label: x.title, value: x.id }));
        setCollectionOptions(options);
        setCollectionsInfo(collections);
      } catch (err) {
        console.log(err);
      }
    };

    const retrievePalette = async () => {
      let payload = {
        order: "-updated_at",
        owner_id: detail?.store_id ? detail?.store_id : account.id,
      };

      try {
        const { data } = await api.palette.list(payload);

        const colors = data["colors"] ? data["colors"] : [];
        const options = colors.map((x) => ({ label: x.name, value: x.id, hex: x.hex }));
        setColorOptions(options);

        //reset color_way values
        const colorIds = detail?.style?.color_way?.map((x) => x.value) || [];
        const color_way = options.filter((x) => colorIds.includes(x.value));
        setValue("color_way", color_way);
      } catch (err) {
        console.log(err);
      }
    };

    const retrieveSizeCharts = async () => {
      try {
        const { data } = await api.sizeChart.list({
          order: "-updated_at",
          owner_id: detail?.store_id ? detail?.store_id : account.id,
        });

        let ownSizeCharts = data["sizes"] ? data["sizes"] : [];

        const shared = await api.sizeChart.sharedList({
          order: "-updated_at",
        });

        let sharedSizeCharts = shared?.data?.sizes || [];
        sharedSizeCharts = sharedSizeCharts.map((x) => ({ ...x, name: "Shared:" + x.name }));

        const allSizeCharts = [...ownSizeCharts, ...sharedSizeCharts];

        const options = allSizeCharts.map((x) => ({
          label: x.name,
          value: x.id,
          meta: {
            id: x.id,
            name: x.name,
            gender: x.gender,
            category: x.category,
            sizes: x.sizes,
            class: x.class,
            attribute: x.attribute,
          },
        }));
        setSizeOptions(options);
        //reset sizeChart value.
        //sizeRange is fixed and duplicated from size chart, it don't need to reset here.
        const sizeChart = options.filter((x) => x.value == detail?.style?.sizeChart?.value);
        if (sizeChart && sizeChart.length > 0) {
          setValue("sizeChart", sizeChart[0]);
        }
      } catch (err) {
        console.log(err);
      }
    };

    const updateImageFiles = async (files) => {
      setUploading(true);
      try {
        //const myImageFiles = await handleUploadFiles(files);
        let myStyle = { ...detail };
        //myStyle["style"]["imageFiles"] = myImageFiles;
        const data = getValues();

        await handleUpdateStyle(data, myStyle);
        setUploading(false);
      } catch (err) {
        setUploading(false);
        if (err?.response?.status == 401) {
          return toast.error(err?.response?.data?.error || err?.response?.statusText);
        }
        toast.error("Upload image files failed!");
      }
    };

    const handleUpdateStyle = async (data, myStyle = detail) => {
      setUploading(true);

      try {
        let product_id = styleId;
        if (!product_id) {
          let payload = {
            title: myStyle?.title || "Untitled",
            status: "design",
            description: "",
            options: [],
            collection_id: null,
          };
          const newCreatedProduct = await api.products.create(payload);
          product_id = newCreatedProduct?.data?.product?.id;
          if (product_id) {
            var url = new window.URL(document.location); // fx. http://host.com/endpoint?abc=123
            url.searchParams.set("id", product_id);
            window.history.replaceState("", "style", url.toString());
          }
        }

        const myImageFiles = await handleUploadFiles(imageFiles);

        let payload = {
          title: myStyle?.title || "Untitled",
          description: data["description"],
          collection_id: data["collection"]?.value || null,
          style: {
            ...myStyle?.style,
            style_number: data["style_number"],
            brand: data["brand"],
            year: data["year"] ? moment(data["year"]).format("YYYY") : null,
            season: data["season"],
            color_way: data["color_way"],
            sizeChart: data["sizeChart"],
            sizeRange: data["sizeRange"],
            imageFiles: myImageFiles,
          },
        };
        if (data["category"]) {
          const node = findParents(tree, data["category"], { findKey: "id", returnKey: "id" });
          payload["type"] = { id: data["category"] };
          payload["path"] = node.join("/");
        } else {
          payload["type"] = null;
          payload["path"] = null;
        }
        console.log(product_id, payload, myStyle);
        await api.products.update(product_id, payload);
        setUploading(false);
        handleRefresh();
        toast.success("Save style information successfully!");
        //handleNext();
      } catch (err) {
        console.log(err);
        setUploading(false);
        if (err?.response?.status == 401) {
          return toast.error(err?.response?.data?.error || err?.response?.statusText);
        }
        toast.error("Save style information failed!");
      }
    };

    const handleReset = () => {
      const styleMeta = detail?.style ? detail.style : {};
      let categoryValue = detail?.type || {};
      let categoryID = undefined; //the category id, leaf node

      if (categoryValue?.id) {
        const categoryNode = findNodeByID(tree, categoryValue.id);
        const node = findParents(tree, categoryValue.id, { findKey: "id", returnKey: "id" });
        if (node && node.length > 0) {
          //have parent node, then set gender to parent node
          let genderNode = findNodeByID(tree, node[0]);
          setSelectedGender(genderNode);
          setCategoryOptions(genderNode?.children || []);
          categoryID = categoryNode?.id;
        } else {
          //doesn't have parent node which means the node is parent itself. set gender to categoryNode
          setSelectedGender(categoryNode);
          setCategoryOptions(categoryNode?.children || []);
        }
      }

      const collectionOpt = { label: detail?.collection?.title, value: detail?.collection?.id };

      setImageFiles(styleMeta?.imageFiles || []);
      if (styleMeta?.imageFiles && styleMeta?.imageFiles.length > 0) {
        setDetailImage(styleMeta.imageFiles[0]);
      }

      //reset color_way values
      const colorIds = detail?.style?.color_way?.map((x) => x.value) || [];
      const color_way = colorOptions.filter((x) => colorIds.includes(x.value));

      //reset sizeChart value.
      //sizeRange is fixed and duplicated from size chart, it don't need to reset here.
      let sizeChart = sizeOptions.filter((x) => x.value == detail?.style?.sizeChart?.value);
      if (sizeChart && sizeChart.length > 0) {
        sizeChart = sizeChart[0];
      }

      reset({
        style_number: styleMeta?.style_number,
        category: categoryID,
        quantity: styleMeta?.quantity,
        cost_per_unit: styleMeta?.cost_per_unit,
        sizeRange: styleMeta?.sizeRange,
        sizeChart: sizeChart,
        color_way: color_way,
        description: detail?.description,
        collection: collectionOpt?.value ? collectionOpt : undefined,
        brand: styleMeta?.brand,
        year: styleMeta?.year ? moment(styleMeta.year) : null,
        season: styleMeta?.season,
      });
      // setStyleTags(
      //   styleMeta?.color_way && typeof styleMeta.color_way == "object" ? styleMeta?.color_way : []
      // );
      setStyleDesc(detail?.description);
    };

    const handleCollectionChange = (e) => {
      if (e?.value) {
        let selectedCollection = collectionsInfo.find((x) => x.id == e.value);
        if (selectedCollection?.id) {
          if (selectedCollection?.metadata?.brand) {
            setValue("brand", selectedCollection?.metadata?.brand);
          }
          if (selectedCollection?.metadata?.year) {
            setValue("year", moment(selectedCollection?.metadata?.year));
          }
          if (selectedCollection?.metadata?.season) {
            setValue("season", selectedCollection?.metadata?.season);
          }
        }
      }
    };

    const handleUploadImages = (data) => {
      const acceptedFiles = [data.file];
      let newFiles = [
        ...imageFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ];

      setImageFiles(newFiles);

      // return api.uploads.create([file])
    };

    let canWrite = !sharedView; //won't allow anyone to change the data
    //||
    // (sharedView && checkPermission(detail?.id, detail?.collection_id, permissionMap));

    //check if the style is locked
    if (detail?.mode == 2) {
      canWrite = false;
    }

    const handleCreateColor = async (label) => {
      label = label.replace(/\W/g, "");
      if (label) {
        try {
          let option = { label, value: label, hex: null };
          await api.palette.create({ name: label });
          setColorOptions((prev) => [...prev, option]);
          let oldValue = getValues("color_way") || [];
          setValue("color_way", [...oldValue, option]);
          toast.success(`${label} has been saved successfully!`);
        } catch (err) {
          console.log(err);
          toast.error(`Color could not be saved!`);
        }
      }
    };

    const handleColorTagClick = async (e, data) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        const color = await retrieveColor(data.value);
        setSelectedColor(color);
        setColorDetailVisible(true);
      } catch (err) {
        toast.error(`Selected color could not be retrieved!`);
      }
    };

    const retrieveColor = async (id) => {
      try {
        const { data } = await api.palette.retrieve(id);
        return data?.color || {};
      } catch (err) {
        console.log(err);
      }
    };

    const selectedSize = getValues("sizeChart");

    const handleGenderChange = (e) => {
      setSelectedGender(e);
      setValue("category", undefined);
      setCategoryOptions(e?.children || []);
    };

    const handleCreateCollection = async (label) => {
      label = label.replace(/\W/g, "");
      if (label) {
        try {
          let payload = {
            title: label,
            description: "",
          };

          const { data } = await api.collections.create(payload);
          const option = {
            label: label,
            value: data?.collection?.id,
          };
          setCollectionOptions((prev) => [...prev, option]);
          setValue("collection", option);
          toast.success(`${label} has been saved successfully!`);
        } catch (err) {
          console.log(err);
          toast.error(`Collection could not be created!`);
        }
      }
    };

    const openImgsViewer = () => setImgsViewerVisible(true);
    const closeImgsViewer = () => setImgsViewerVisible(false);

    const handleFullScreen = (e, url) => {
      const index = imageFiles.findIndex((x) => x.url == url || x.preview == url);
      if (index > -1) {
        setImgsViewerCurrent(index);
        openImgsViewer();
      }
    };

    return (
      <SuiBox>
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <SuiBox>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={5}>
              <Card sx={{ p: 3, height: "100%" }}>
                <SuiBox>
                  <SuiBox ml={0.5}>
                    <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                      Style Images
                    </SuiTypography>
                  </SuiBox>
                  <SuiBox mb={1.5} ml={0.5} mt={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="regular"
                      color="text"
                    >
                      The first image will be your primary image
                    </SuiTypography>
                  </SuiBox>
                  <SuiBox sx={{ maxWidth: 320, margin: "auto" }}>
                    {imageFiles && imageFiles.length > 0 ? (
                      <div style={{ position: "relative" }}>
                        <SuiButton
                          variant="contained"
                          iconOnly
                          circular
                          sx={{
                            position: "absolute",
                            top: "50%",
                            right: 0,
                            zIndex: 4,
                            minWidth: "2.5rem",
                            opacity: 0.6,
                            "&:hover": { opacity: 0.9 },
                          }}
                          onClick={() => {
                            const index = imageFiles.findIndex((x) =>
                              x.preview
                                ? x.preview == detailImage.preview
                                : x.url == detailImage.url
                            );
                            if (index > -1) {
                              if (index < imageFiles.length - 1) {
                                setDetailImage(imageFiles[index + 1]);
                              }
                            }
                          }}
                        >
                          <ArrowForwardIosIcon />
                        </SuiButton>
                        <SuiButton
                          variant="contained"
                          iconOnly
                          circular
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: 0,
                            zIndex: 4,
                            minWidth: "2.5rem",
                            opacity: 0.6,
                            textAlign: "center",
                            margin: "auto",
                            "&:hover": { opacity: 0.9 },
                          }}
                          onClick={() => {
                            const index = imageFiles.findIndex((x) =>
                              x.preview
                                ? x.preview == detailImage.preview
                                : x.url == detailImage.url
                            );
                            if (index > -1) {
                              if (index > 0) {
                                setDetailImage(imageFiles[index - 1]);
                              }
                            }
                          }}
                        >
                          <ArrowBackIosNewIcon />
                        </SuiButton>
                        <SuiBox
                          onClick={(e) =>
                            handleFullScreen(e, detailImage?.url || detailImage.preview)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          <ImageCardWrapper
                            data={detailImage}
                            mode={"normal"}
                            width={320}
                            height={400}
                            borderRadius="xl"
                            pdfPagination={true}
                          />
                        </SuiBox>
                      </div>
                    ) : (
                      <Upload.Dragger
                        name="design-images"
                        showUploadList={false}
                        customRequest={handleUploadImages}
                        accept="image/*, application/pdf"
                        className="rounded-2xl h-full"
                      >
                        <SuiBox
                          sx={{
                            overflow: "hidden",
                            width: "100%",
                            backgroundImage: ({
                              functions: { rgba, linearGradient },
                              palette: { gradients },
                            }) =>
                              `${linearGradient(
                                rgba(gradients.secondary.main, 0.2),
                                rgba(gradients.secondary.state, 0.4)
                              )}`,
                            height: "400px",
                            margin: "-16px 0px",
                            boxShadow: ({ boxShadows: { md } }) => md,
                            objectFit: "contain",
                            objectPosition: "center",
                          }}
                          display="flex"
                          alignItems="center"
                          position="relative"
                          minHeight="400px"
                          borderRadius="xl"
                        />
                      </Upload.Dragger>
                    )}
                  </SuiBox>
                </SuiBox>
                <SuiBox mt={2}>
                  {canWrite ? (
                    <DropZone
                      files={imageFiles}
                      setfiles={setImageFiles}
                      options={{
                        accept: { "image/*": [".jpeg", ".png"], "application/pdf": [".pdf"] },
                      }}
                      onFileClick={(file) => setDetailImage(file)}
                    />
                  ) : (
                    <div
                      style={{
                        border: "2px dashed gray",
                        borderRadius: "15px",
                        margin: "0 4px",
                        width: "100%",
                        height: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        padding: "8px",
                      }}
                    >
                      {imageFiles && imageFiles.length > 0 ? (
                        <FilePreviewCard
                          files={imageFiles}
                          setfiles={false}
                          handleDelete={false}
                          onFileClick={(file) => setDetailImage(file)}
                        />
                      ) : (
                        <SuiBox sx={{ height: 140, px: 2 }} display="flex" alignItems="center">
                          <SuiTypography
                            variant="h6"
                            color="text"
                            fontWeight="regular"
                            textTransform="capitalize"
                          >
                            No available data!
                          </SuiTypography>
                        </SuiBox>
                      )}
                    </div>
                  )}
                </SuiBox>
              </Card>
            </Grid>

            <Grid item xs={12} sm={12} md={7}>
              <form onSubmit={handleSubmit((data) => handleUpdateStyle(data, detail))}>
                <fieldset disabled={!canWrite}>
                  <Card sx={{ p: 3, height: "100%" }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={12} md={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                              required
                            >
                              Style Number
                            </SuiTypography>
                          </SuiBox>
                          <SuiInput
                            placeholder="Style Number"
                            required
                            {...register("style_number", {
                              required: "A styled number is required!",
                            })}
                            disabled={!canWrite}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Brand
                            </SuiTypography>
                          </SuiBox>
                          <SuiInput placeholder="Brand" {...register("brand")} />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Collection
                            </SuiTypography>
                          </SuiBox>
                          <SuiBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <SuiBox sx={{ flexGrow: "1" }}>
                              <Controller
                                name="collection"
                                control={control}
                                render={({ field: { onChange, onBlur, value, name, ref } }) => (
                                  <SuiSelect
                                    isDisabled={sharedView}
                                    onChange={(e) => {
                                      handleCollectionChange(e);
                                      onChange(e);
                                    }}
                                    value={value}
                                    name={name}
                                    ref={ref}
                                    options={collectionOptions}
                                    backspaceRemovesValue
                                    isClearable
                                    placeholder="Collection"
                                    isCreatable
                                    onCreateOption={handleCreateCollection}
                                  />
                                )}
                              />
                            </SuiBox>
                            {!sharedView && canWrite ? (
                              <SuiTypography
                                variant="body1"
                                fontWeight="regular"
                                color={"dark"}
                                sx={{
                                  maxWidth: 80,
                                  lineHeight: 1,
                                  cursor: "pointer",
                                  textAlign: "center",
                                  textDecoration: "underline",
                                }}
                                onClick={() => setCreateCollectionVisible(true)}
                              >
                                New Collection
                              </SuiTypography>
                            ) : null}
                          </SuiBox>
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Year
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="year"
                            control={control}
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                picker="year"
                                placeholder="Year"
                                style={{ width: "100%", height: 40 }}
                                popupStyle={{
                                  maxHeight: 400,
                                  overflow: "auto",
                                  zIndex: 9999,
                                }}
                                disabled={!canWrite}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Season
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="season"
                            control={control}
                            render={({ field }) => (
                              <SuiSelect
                                {...field}
                                options={seasonOptions}
                                placeholder="Season"
                                backspaceRemovesValue
                                isClearable
                                isDisabled={!canWrite}
                                readOnly={!canWrite}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Gender
                            </SuiTypography>
                          </SuiBox>

                          <SuiSelect
                            options={tree}
                            value={selectedGender}
                            placeholder="Gender"
                            onChange={handleGenderChange}
                            backspaceRemovesValue
                            isClearable={canWrite ? true : false}
                            isDisabled={sharedView || !canWrite}
                            readOnly={sharedView}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Category
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            control={control}
                            name="category"
                            render={({ field: { onChange, onBlur, value, name, ref } }) => (
                              <UTreeSelect
                                tree={categoryOptions}
                                fieldNames={{ label: "label", value: "id", children: "children" }}
                                onChange={(e) => {
                                  onChange(e);
                                }}
                                allowClear={true}
                                value={value}
                                placeholder="Category"
                                disabled={!canWrite}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                              info={`Type your color name to search or create.`}
                            >
                              Colorways
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="color_way"
                            control={control}
                            render={({ field }) => (
                              <SuiSelect
                                {...field}
                                options={colorOptions}
                                placeholder="Type your color name to sear or create"
                                backspaceRemovesValue
                                isClearable={canWrite ? true : false}
                                isCreatable={sharedView ? false : true}
                                isMulti
                                onCreateOption={handleCreateColor}
                                isDisabled={sharedView || !canWrite}
                                readOnly={sharedView}
                                handleTagClick={handleColorTagClick}
                              />
                            )}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Size Chart
                              {selectedSize?.value ? (
                                <SuiTypography
                                  component="span"
                                  variant="body2"
                                  sx={{ ml: 1, fontWeight: "regular", textDecoration: "underline" }}
                                  color="text"
                                >
                                  <Link to={`/setup/size-master/${selectedSize?.value}`}>
                                    detail
                                  </Link>
                                </SuiTypography>
                              ) : null}
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="sizeChart"
                            control={control}
                            render={({ field }) => {
                              return (
                                <SuiBox>
                                  <SuiSelect
                                    {...field}
                                    placeholder="Size Chart"
                                    isSearchable={true}
                                    options={sizeOptions}
                                    isClearable={canWrite ? true : false}
                                    isDisabled={sharedView || !canWrite}
                                    escapeClearsValue
                                  />{" "}
                                </SuiBox>
                              );
                            }}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SuiTypography
                              component="label"
                              variant="caption"
                              fontWeight="bold"
                              textTransform="capitalize"
                            >
                              Size Range
                            </SuiTypography>
                          </SuiBox>
                          <Controller
                            name="sizeRange"
                            control={control}
                            render={({ field }) => {
                              return (
                                <SuiBox>
                                  <SuiInput {...field} placeholder="Size Range" />{" "}
                                </SuiBox>
                              );
                            }}
                          />
                        </SuiBox>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <SuiBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                          height="100%"
                        >
                          <SuiBox mb={1} ml={0.5} mt={1} lineHeight={0} display="inline-block">
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              Description
                            </SuiTypography>
                          </SuiBox>

                          <SuiInput
                            multiline
                            rows="5"
                            componentsProps={{ input: { maxlength: STYLE_DESC_MAX_LENGTH } }}
                            {...register("description", {
                              maxLength: {
                                value: STYLE_DESC_MAX_LENGTH,
                                message: `Maxmium length should not exceed ${STYLE_DESC_MAX_LENGTH} characters`,
                              },
                              value: styleDesc,
                              onChange: (e) => {
                                setStyleDesc(e.target.value);
                                setValue("description", e.target.value);
                              },
                            })}
                            placeholder="Your style description..."
                            disabled={!canWrite}
                          />

                          <SuiBox display="flex" justifyContent="space-between">
                            <div>
                              <ErrorMessage
                                errors={errors}
                                name="description"
                                render={({ message }) => (
                                  <SuiTypography
                                    variant="text"
                                    fontWeight="regular"
                                    fontSize="12px"
                                    color="error"
                                    sx={{ ml: 1 }}
                                  >
                                    {message}
                                  </SuiTypography>
                                )}
                              />
                            </div>
                            <SuiTypography
                              variant="text"
                              color="secondary"
                              fontWeight="regular"
                              fontSize="14px"
                            >
                              {styleDesc ? styleDesc.length : 0}/{STYLE_DESC_MAX_LENGTH}
                            </SuiTypography>
                          </SuiBox>
                        </SuiBox>
                      </Grid>
                    </Grid>
                    {canWrite ? (
                      <SuiBox
                        pt={3}
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        flexDirection={{ xs: "column", sm: "row" }}
                      >
                        {sharedView ? (
                          <div />
                        ) : (
                          <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                              <SuiButton
                                variant="gradient"
                                color="secondary"
                                onClick={() => setDuplicateStyleVisible(true)}
                              >
                                Duplicate
                              </SuiButton>
                            </SuiBox>
                          </SuiBox>
                        )}
                        <SuiBox
                          display="flex"
                          justifyContent="start"
                          alignItems="center"
                          flexDirection={{ xs: "column", sm: "row" }}
                        >
                          <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                            <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                              Save
                            </SuiButton>
                          </SuiBox>
                        </SuiBox>
                      </SuiBox>
                    ) : null}
                  </Card>
                </fieldset>
              </form>
            </Grid>
          </Grid>
          {/* {isInPortfolio(detail.id, portfolio_content) ? (
          <SuiBox>
            <Accordion
              sx={{
                mt: 2,
              }}
              className="rounded-t-lg rounded-b-lg"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <SuiTypography variant="h6">Comments from portfolio</SuiTypography>
              </AccordionSummary>
              <AccordionDetails>
                <Comments subjectid={"portfolio_" + detail.id} />
              </AccordionDetails>
            </Accordion>
          </SuiBox>
        ) : null} */}
        </SuiBox>
        {createCollectionVisible ? (
          <CreateCollectionModal
            handleClose={() => setCreateCollectionVisible(false)}
            visible={createCollectionVisible}
            isEdit={false}
            handleRefresh={retrieveCollections}
          />
        ) : null}
        {duplicateStyleVisible ? (
          <FullCreateStyleModal
            handleClose={() => setDuplicateStyleVisible(false)}
            //handleOk={(data) => handleDuplicateStyle(data)}
            visible={duplicateStyleVisible}
            isDuplicate={true}
            style={detail}
            handleRefresh={handleRefresh}
          />
        ) : null}
        {colorDetailVisible && selectedColor ? (
          <CreateColorModal
            handleClose={() => setColorDetailVisible(false)}
            visible={colorDetailVisible}
            isEdit={true}
            handleRefresh={null}
            color={selectedColor}
            readOnly
          />
        ) : null}

        <Lightbox
          open={imgsViewerVisible}
          close={closeImgsViewer}
          slides={imageFiles.map((x, index) => ({ src: x?.url || x?.preview, id: index }))}
          plugins={[Zoom, Download, Thumbnails]}
          zoom={{ maxZoomPixelRatio: 3 }}
          index={imgsViewerCurrent}
        />
      </SuiBox>
    );
  }
);

export default DesignStage;
